/**
 * MT User Stream — connects to /relay/mt-ws instead of proxying raw Kalshi WS.
 *
 * The relay's UserStreamRouter subscribes to Kalshi once and routes events
 * per-trader. This client receives only events owned by the authenticated user.
 *
 * Same callback interface as createUserStream() for drop-in replacement.
 */

import type { FillNotification } from '../../types';
import type { MtStreamEvent, MtWsFrame } from '@galactus/shared';
import { tryParseFill } from './userStream';
import { boundaryWarn } from '../infra/boundaryLog';

export interface MtUserStream {
  connect(jwtToken: string): void;
  onFill(callback: (fill: FillNotification) => void): void;
  onOrderEvent(callback: (event: unknown) => void): void;
  onError(callback: (error: Error) => void): void;
  disconnect(): void;
  isConnected(): boolean;
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export function createMtUserStream(relayBaseUrl?: string): MtUserStream {
  let ws: WebSocket | null = null;
  let connected = false;
  let token: string | null = null;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  const fillCallbacks = new Set<(fill: FillNotification) => void>();
  const orderCallbacks = new Set<(event: unknown) => void>();
  const errorCallbacks = new Set<(error: Error) => void>();

  const getWsUrl = (): string => {
    if (relayBaseUrl) {
      const base = relayBaseUrl.replace(/^http/, 'ws');
      return `${base}/relay/mt-ws?token=${encodeURIComponent(token!)}`;
    }
    // Default: same host as page, WSS
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/relay/mt-ws?token=${encodeURIComponent(token!)}`;
  };

  const emitFill = (fill: FillNotification) => {
    fillCallbacks.forEach((cb) => cb(fill));
  };

  const emitOrderEvent = (event: unknown) => {
    orderCallbacks.forEach((cb) => cb(event));
  };

  const emitError = (error: Error) => {
    errorCallbacks.forEach((cb) => cb(error));
  };

  const handleMtEvent = (event: MtStreamEvent) => {
    if (event.type === 'fill') {
      // Parse the raw data into a FillNotification using shared parser
      const fill = tryParseFill(event.data);
      if (fill) {
        // Propagate source tier so ActivityRail can filter by self/room/account
        if (event.source) fill.source = event.source;
        emitFill(fill);
      } else {
        boundaryWarn('stream', 'MT fill event unparseable', { orderId: event.orderId });
      }
    } else if (event.type === 'order_update' || event.type === 'order_cancel') {
      emitOrderEvent(event.data);
    }
  };

  const handleMessage = (rawData: MessageEvent) => {
    try {
      const frame = JSON.parse(rawData.data) as MtWsFrame;

      if (frame.type === 'mt:event') {
        handleMtEvent(frame.event);
      } else if (frame.type === 'mt:room-event') {
        // Room feed events — same handling but source is already tagged 'room'
        handleMtEvent(frame.event);
      } else if (frame.type === 'mt:broadcast') {
        // Position-level broadcasts — pass through as order events
        emitOrderEvent(frame.data);
      }
    } catch {
      // Ignore unparseable
    }
  };

  const doConnect = () => {
    if (destroyed || !token) return;

    const url = getWsUrl();
    ws = new WebSocket(url);

    ws.onopen = () => {
      connected = true;
      reconnectAttempt = 0;
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      connected = false;
      ws = null;
      if (!destroyed) {
        scheduleReconnect();
      }
    };

    ws.onerror = (ev) => {
      emitError(new Error(`MT WS error: ${String(ev)}`));
    };
  };

  const scheduleReconnect = () => {
    if (destroyed) return;
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt), RECONNECT_MAX_MS);
    reconnectAttempt++;
    reconnectTimer = setTimeout(() => doConnect(), delay);
  };

  return {
    connect(jwtToken: string) {
      token = jwtToken;
      destroyed = false;
      doConnect();
    },

    onFill(callback: (fill: FillNotification) => void) {
      fillCallbacks.add(callback);
    },

    onOrderEvent(callback: (event: unknown) => void) {
      orderCallbacks.add(callback);
    },

    onError(callback: (error: Error) => void) {
      errorCallbacks.add(callback);
    },

    disconnect() {
      destroyed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.close();
        ws = null;
      }
      connected = false;
      token = null;
      fillCallbacks.clear();
      orderCallbacks.clear();
      errorCallbacks.clear();
    },

    isConnected() {
      return connected;
    },
  };
}
